import { useRef, useState } from 'react';
import { components, ControlProps } from 'react-select';

import Icon from '../../../../components/Icon/Icon';
import { useLocale } from '../../../../context/LocaleContext';
import { TransportTypesE } from '../../../../declarations/enums/common';
import { TransportDetailsI, TransportTypeT } from '../../../../declarations/interfaces/transports';
import useOuterClick from '../../../../hooks/useOuterClick';
import { ButtonStyled, TransportSelectWrapperStyled } from './TransportSelect.styled';

export type SelectedTransportT = {
  transportType: TransportTypesE;
  vehicleType: string;
};

type TransportSelectPropsT = {
  vehicleTypeValue?: TransportDetailsI | null;
  onSelect: (val: SelectedTransportT) => void;
} & TransportTypeT;

type ValueContainerT = { transportType: TransportTypesE } & ControlProps<TransportDetailsI, false>;

const OverrideValueContainer = ({
  transportType,
  children,
  innerRef,
  isFocused,
  ...props
}: ValueContainerT) => {
  const {
    hasValue,
    selectProps: { menuIsOpen },
  } = props;

  return (
    <components.ValueContainer {...props}>
      <ButtonStyled isFullWidth isInverted={hasValue || menuIsOpen}>
        <Icon fileName={`transports/${transportType.toLowerCase()}`} /> {children}
      </ButtonStyled>
    </components.ValueContainer>
  );
};

const TransportSelect: React.FC<TransportSelectPropsT> = ({
  vehicles,
  transportType,
  vehicleTypeValue,
  onSelect,
}) => {
  const { selectedLocale } = useLocale();
  const [isOpened, setIsOpened] = useState(false);
  const handleChangeTransport = ({
    transportType: selectedTransportType,
    vehicleType,
  }: SelectedTransportT) => {
    onSelect({ transportType: selectedTransportType, vehicleType });
    setIsOpened(false);
  };

  // workaround, dropdown won't close when click on the another dropdown
  const selectWrapperRef = useRef(null);

  useOuterClick(() => {
    setIsOpened(false);
  }, selectWrapperRef);

  return (
    <div ref={selectWrapperRef}>
      <TransportSelectWrapperStyled
        menuIsOpen={isOpened}
        onMenuOpen={() => setIsOpened(true)}
        value={vehicleTypeValue}
        options={vehicles}
        onChange={({ code }) => handleChangeTransport({ transportType, vehicleType: code })}
        controlShouldRenderValue={false}
        components={{
          ValueContainer: props =>
            OverrideValueContainer({ transportType, ...props } as ValueContainerT),
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
          Placeholder: () => null,
          IndicatorsContainer: () => null,
        }}
        getOptionLabel={option => option['displayName'][selectedLocale]}
        getOptionValue={option => option['code']}
      />
    </div>
  );
};

export default TransportSelect;
